import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  ChevronRight,
  DoorOpen,
  Ruler,
  PanelTop,
  Sparkles,
  X,
  ChevronLeft,
  ZoomIn,
} from "lucide-react";

const galleryItems = [
  {
    id: 1,
    category: "doors",
    title: "Victorian Door Restoration",
    location: "Stockport",
    description: "Original Victorian doors stripped, rehung with new ironmongery. Doors now close properly with no sticking.",
    beforeImage: "https://images.unsplash.com/photo-1586042287097-51da461da5d3?w=600&h=400&fit=crop",
    afterImage: "https://images.unsplash.com/photo-1682450195449-32ab08ddf7e7?w=600&h=400&fit=crop",
  },
  {
    id: 2,
    category: "doors",
    title: "New Build Door Fitting",
    location: "Cheadle",
    description: "8 internal doors hung throughout property. Includes fire doors to kitchen and garage.",
    beforeImage: "https://images.unsplash.com/photo-1691234284616-64df61584b2e?w=600&h=400&fit=crop",
    afterImage: "https://images.unsplash.com/photo-1685881495881-25c035670d82?w=600&h=400&fit=crop",
  },
  {
    id: 3,
    category: "trimwork",
    title: "Hallway Skirting & Architraves",
    location: "Bramhall",
    description: "Full replacement of damaged skirting boards with Torus profile. Neat mitres throughout.",
    beforeImage: "https://images.unsplash.com/photo-1560185127-6ed189bf02f4?w=600&h=400&fit=crop",
    afterImage: "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=600&h=400&fit=crop",
  },
  {
    id: 4,
    category: "panelling",
    title: "Living Room Shaker Panelling",
    location: "Altrincham",
    description: "Half-height shaker-style panelling fitted to feature wall. Ready for painting.",
    beforeImage: "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=600&h=400&fit=crop",
    afterImage: "https://images.unsplash.com/photo-1701422522223-355c7a6f5648?w=600&h=400&fit=crop",
  },
  {
    id: 5,
    category: "panelling",
    title: "Feature Wall Panelling",
    location: "Sale",
    description: "Modern slat panelling installed behind TV unit. Clean lines and level installation.",
    beforeImage: "https://images.unsplash.com/photo-1493809842364-78817add7ffb?w=600&h=400&fit=crop",
    afterImage: "https://images.unsplash.com/photo-1701421047999-eacdc479e8bd?w=600&h=400&fit=crop",
  },
  {
    id: 6,
    category: "sanding",
    title: "Oak Floor Restoration",
    location: "Didsbury",
    description: "Original oak floorboards sanded and sealed. Dust-free extraction used throughout.",
    beforeImage: "https://images.unsplash.com/photo-1673924968581-c18b53d64e22?w=600&h=400&fit=crop",
    afterImage: "https://images.unsplash.com/photo-1693948568453-a3564f179a84?w=600&h=400&fit=crop",
  },
];

const categories = [
  { id: "all", label: "All Work", icon: null },
  { id: "doors", label: "Doors", icon: DoorOpen },
  { id: "trimwork", label: "Trimwork", icon: Ruler },
  { id: "panelling", label: "Panelling", icon: PanelTop },
  { id: "sanding", label: "Sanding", icon: Sparkles },
];

const GalleryPage = () => {
  const [activeCategory, setActiveCategory] = useState("all");
  const [lightboxItem, setLightboxItem] = useState(null);
  const [showAfter, setShowAfter] = useState(true);

  const filteredItems = activeCategory === "all"
    ? galleryItems
    : galleryItems.filter(item => item.category === activeCategory);

  const openLightbox = (item) => {
    setLightboxItem(item);
    setShowAfter(true);
  };

  const closeLightbox = () => {
    setLightboxItem(null);
  };

  const navigateLightbox = (direction) => {
    const currentIndex = filteredItems.findIndex(item => item.id === lightboxItem.id);
    const nextIndex = direction === "next"
      ? (currentIndex + 1) % filteredItems.length
      : (currentIndex - 1 + filteredItems.length) % filteredItems.length;
    setLightboxItem(filteredItems[nextIndex]);
    setShowAfter(true);
  };

  return (
    <div>
      <Helmet>
        <title>Our Work | Before & After Gallery | Expert Carpenter Greater Manchester</title>
        <meta name="description" content="See examples of our carpentry work across Greater Manchester. Before and after photos of door fitting, skirting boards, wall panelling and floor sanding." />
        <link rel="canonical" href="https://expert-carpenter.co.uk/gallery" />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#1a1a1a] to-[#2d2d2d] py-12 sm:py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-[#C9A227] font-semibold tracking-wider uppercase mb-3 text-sm">Portfolio</p>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
            Before & After Gallery
          </h1>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            Real examples of our work across Greater Manchester. Every job completed to a high standard.
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="bg-white border-b border-gray-200 sticky top-14 sm:top-16 md:top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-2 py-4 overflow-x-auto scrollbar-hide">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  activeCategory === cat.id
                    ? "bg-[#2D5A4A] text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
                data-testid={`filter-${cat.id}`}
              >
                {cat.icon && <cat.icon className="h-4 w-4" />}
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-10 sm:py-14 md:py-20 bg-[#f8f7f5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredItems.map((item) => (
              <Card
                key={item.id}
                className="group overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer"
                onClick={() => openLightbox(item)}
                data-testid={`gallery-item-${item.id}`}
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={item.afterImage}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                  
                  {/* View indicator */}
                  <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-full flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <ZoomIn className="h-4 w-4 text-[#2D5A4A]" />
                    <span className="text-xs font-medium text-[#2D5A4A]">View Before/After</span>
                  </div>
                  
                  {/* Category badge */}
                  <div className="absolute top-3 left-3">
                    <span className="text-xs bg-[#C9A227] text-black px-2 py-1 rounded-full font-medium capitalize">
                      {item.category}
                    </span>
                  </div>
                  
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="text-lg font-bold text-white">{item.title}</h3>
                    <p className="text-white/80 text-sm">{item.location}</p>
                  </div>
                </div>
                <CardContent className="p-4">
                  <p className="text-gray-600 text-sm line-clamp-2">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {filteredItems.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">No projects found in this category.</p>
            </div>
          )}
        </div>
      </section>

      {/* Lightbox Modal */}
      {lightboxItem && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-gray-300 p-2"
            onClick={closeLightbox}
            aria-label="Close"
          >
            <X className="h-8 w-8" />
          </button>
          
          <button
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 p-2 bg-white/10 rounded-full"
            onClick={(e) => { e.stopPropagation(); navigateLightbox("prev"); }}
            aria-label="Previous"
          >
            <ChevronLeft className="h-8 w-8" />
          </button>
          
          <button
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 p-2 bg-white/10 rounded-full"
            onClick={(e) => { e.stopPropagation(); navigateLightbox("next"); }}
            aria-label="Next"
          >
            <ChevronRight className="h-8 w-8" />
          </button>

          <div
            className="max-w-4xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Before/After Toggle */}
            <div className="flex justify-center gap-2 mb-4">
              <button
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                  !showAfter
                    ? "bg-[#C9A227] text-black"
                    : "bg-white/20 text-white hover:bg-white/30"
                }`}
                onClick={() => setShowAfter(false)}
              >
                Before
              </button>
              <button
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                  showAfter
                    ? "bg-[#C9A227] text-black"
                    : "bg-white/20 text-white hover:bg-white/30"
                }`}
                onClick={() => setShowAfter(true)}
              >
                After
              </button>
            </div>

            {/* Image */}
            <div className="relative aspect-[16/10] rounded-lg overflow-hidden bg-gray-900">
              <img
                src={showAfter ? lightboxItem.afterImage : lightboxItem.beforeImage}
                alt={`${lightboxItem.title} - ${showAfter ? "After" : "Before"}`}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-4 left-4 bg-black/70 px-3 py-1.5 rounded-full">
                <span className="text-white text-sm font-medium">{showAfter ? "After" : "Before"}</span>
              </div>
            </div>

            {/* Details */}
            <div className="mt-4 text-center">
              <h3 className="text-xl font-bold text-white">{lightboxItem.title}</h3>
              <p className="text-[#C9A227] text-sm mt-1">{lightboxItem.location}</p>
              <p className="text-gray-400 text-sm mt-2 max-w-2xl mx-auto">{lightboxItem.description}</p>
            </div>
          </div>
        </div>
      )}

      {/* CTA Section */}
      <section className="py-12 sm:py-16 bg-[#2D5A4A]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4">
            Want Similar Results?
          </h2>
          <p className="text-white/80 mb-6 max-w-xl mx-auto">
            Get in touch for a free, no-obligation quote. I'll get back to you quickly to discuss your project.
          </p>
          <Link to="/contact">
            <Button
              size="lg"
              className="bg-[#C9A227] hover:bg-[#b8922a] text-black font-bold h-12 px-8"
              data-testid="gallery-cta-btn"
            >
              Get a Free Quote
              <ChevronRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default GalleryPage;
